/**
 * useSettingsStore — local SettingsPage state, mirrors H5 utility settings.
 *
 * H5 source (in-memory only):
 *   - `user.defaultIdentity`              main.js:570, 583, 943, 5788-5792, 5917-5920
 *   - `document.body.classList.reduce-motion`     main.js:5794-5796, 5921-5924
 *   - `document.body.classList.compact-density`   main.js:5797-5802, 5925-5927
 *   - `state.profilePrivate`              main.js:5803-5805, 5928-5932
 *
 * React app persists all four under `tree-hole.settings.v1` so a returning
 * device keeps the same preferences without relying on a backend round-trip
 * (H5 itself does not call any API for these — see docs/api/yaml.yml).
 */

import { create } from "zustand";

import { STORAGE_KEYS } from "@/shared/storage/keys.js";
import { readStorageItem, writeStorageItem } from "@/shared/storage/localStorage.js";

export type DefaultIdentity = "public" | "anonymous";
export type Density = "comfortable" | "compact";

export type SettingsState = {
  defaultIdentity: DefaultIdentity;
  reduceMotion: boolean;
  density: Density;
  profilePrivate: boolean;
};

export type SettingsActions = {
  setDefaultIdentity: (value: DefaultIdentity) => void;
  setReduceMotion: (value: boolean) => void;
  setDensity: (value: Density) => void;
  setProfilePrivate: (value: boolean) => void;
};

const DEFAULTS: SettingsState = {
  defaultIdentity: "public",
  reduceMotion: false,
  density: "comfortable",
  profilePrivate: false,
};

function isDefaultIdentity(value: unknown): value is DefaultIdentity {
  return value === "public" || value === "anonymous";
}

function isDensity(value: unknown): value is Density {
  return value === "comfortable" || value === "compact";
}

function readInitialSettings(): SettingsState {
  const raw = readStorageItem(STORAGE_KEYS.settings);
  if (!raw) return DEFAULTS;
  try {
    const parsed = JSON.parse(raw) as Partial<SettingsState> | null;
    if (!parsed || typeof parsed !== "object") return DEFAULTS;
    return {
      defaultIdentity: isDefaultIdentity(parsed.defaultIdentity)
        ? parsed.defaultIdentity
        : DEFAULTS.defaultIdentity,
      reduceMotion:
        typeof parsed.reduceMotion === "boolean" ? parsed.reduceMotion : DEFAULTS.reduceMotion,
      density: isDensity(parsed.density) ? parsed.density : DEFAULTS.density,
      profilePrivate:
        typeof parsed.profilePrivate === "boolean"
          ? parsed.profilePrivate
          : DEFAULTS.profilePrivate,
    };
  } catch {
    return DEFAULTS;
  }
}

function persist(next: SettingsState) {
  writeStorageItem(STORAGE_KEYS.settings, JSON.stringify(next));
}

export const useSettingsStore = create<SettingsState & SettingsActions>((set, get) => ({
  ...readInitialSettings(),
  setDefaultIdentity(value) {
    const next = { ...get(), defaultIdentity: value };
    persist(next);
    set({ defaultIdentity: value });
  },
  setReduceMotion(value) {
    const next = { ...get(), reduceMotion: value };
    persist(next);
    set({ reduceMotion: value });
  },
  setDensity(value) {
    const next = { ...get(), density: value };
    persist(next);
    set({ density: value });
  },
  setProfilePrivate(value) {
    const next = { ...get(), profilePrivate: value };
    persist(next);
    set({ profilePrivate: value });
  },
}));
